import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { initialsOf as initials, useLearnerContext } from './useLearnerContext';
import './StudentSwitcher.css';

interface StudentSwitcherProps {
  /** When true the sidebar is collapsed to icon-only width. */
  isCollapsed?: boolean;
}

/**
 * Renders at the bottom of the sidebar for PARENT users.
 * Shows the currently active student and lets the parent switch between their
 * registered students.  Hidden for non-PARENT roles.
 */
const StudentSwitcher: React.FC<StudentSwitcherProps> = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const {
    isParent,
    activeContext,
    isSwitching,
    error,
    students,
    loaded,
    fetchError,
    select,
    returnToParent,
  } = useLearnerContext();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSelect = useCallback(
    (studentId: string) => {
      select(studentId);
      setIsOpen(false);
    },
    [select],
  );

  /**
   * Leave student context and go back to seeing the account as the parent.
   * Without this there was no way out: selecting a child was a one-way door.
   *
   * The server is cleared first (PRT-08-FR-07). On failure the dropdown stays
   * open with the reason, rather than navigating to a parent view the next
   * refresh would undo.
   */
  const handleClear = useCallback(async () => {
    if (await returnToParent()) {
      setIsOpen(false);
      navigate('/parent/dashboard');
    }
  }, [returnToParent, navigate]);

  if (!isParent) return null;

  const displayName = activeContext?.studentName ?? 'Ver como padre';
  const hasActive = Boolean(activeContext);

  return (
    <div
      className={`student-switcher ${isCollapsed ? 'student-switcher--collapsed' : ''}`}
      ref={dropdownRef}
    >
      <button
        className="student-switcher__trigger"
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={isCollapsed ? displayName : undefined}
      >
        {/* Avatar */}
        <span
          className={`student-switcher__avatar ${
            hasActive ? 'student-switcher__avatar--active' : ''
          }`}
        >
          {hasActive ? (
            initials(activeContext!.studentName)
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
            </svg>
          )}
        </span>

        {!isCollapsed && (
          <>
            <span className="student-switcher__label">
              <span className="student-switcher__hint">{hasActive ? 'Viendo como' : 'Vista'}</span>
              <span className="student-switcher__name">{displayName}</span>
            </span>
            <svg
              className={`student-switcher__chevron ${
                isOpen ? 'student-switcher__chevron--open' : ''
              }`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div className="student-switcher__dropdown" role="listbox" aria-label="Seleccionar alumno">
          {/* Escape hatch. Selecting a child used to be a one-way door: there
              was no way back to the parent's own view short of logging out. */}
          {hasActive && (
            <button
              className="student-switcher__option"
              onClick={handleClear}
              disabled={isSwitching}
            >
              <span className="student-switcher__option-avatar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                </svg>
              </span>
              <span className="student-switcher__option-info">
                <span className="student-switcher__option-name">Ver como padre</span>
                <span className="student-switcher__option-grade">Salir de la vista del alumno</span>
              </span>
            </button>
          )}

          {error && (
            <p className="student-switcher__message" role="alert">
              No pudimos cambiar la vista. Inténtalo de nuevo.
            </p>
          )}

          {/* Loading is its own state (S1.3). This used to fall through to
              "Todavía no tienes alumnos" while the request was still in flight,
              so a parent who has students was told they have none. */}
          {!loaded && !fetchError ? (
            <p className="student-switcher__message" role="status">
              Cargando alumnos…
            </p>
          ) : fetchError ? (
            <p className="student-switcher__message">Error al cargar alumnos</p>
          ) : students.length === 0 ? (
            <p className="student-switcher__message">Todavía no tienes alumnos</p>
          ) : (
            students.map((s) => {
              const name = `${s.firstName} ${s.lastName}`;
              const isActive = activeContext?.studentId === s.studentId;
              return (
                <button
                  key={s.studentId}
                  role="option"
                  aria-selected={isActive}
                  className={`student-switcher__option ${
                    isActive ? 'student-switcher__option--active' : ''
                  }`}
                  onClick={() => handleSelect(s.studentId)}
                  disabled={isSwitching}
                >
                  <span className="student-switcher__option-avatar">{initials(name)}</span>
                  <span className="student-switcher__option-info">
                    <span className="student-switcher__option-name">{name}</span>
                    <span className="student-switcher__option-grade">Grado {s.grade}</span>
                  </span>
                  {isActive && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-label="Activo">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })
          )}

          <Link
            to="/parent/students/new"
            className="student-switcher__option"
            onClick={() => setIsOpen(false)}
            style={{ textDecoration: 'none' }}
          >
            <span className="student-switcher__option-avatar">+</span>
            <span className="student-switcher__option-info">
              <span className="student-switcher__option-name">Agregar alumno</span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default StudentSwitcher;
